import React, { useState } from "react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { BaseFormFields, useBaseForm } from "./BaseFormFields";
import { UserSearchSelect } from "../ui/UserSearchSelect";
import { useApp } from "../../context/AppContext";

const READONLY_FIELDS = [
    ["employee_name", "Collaborateur"],
    ["from_department_name", "Département actuel"],
    ["to_department_name", "Nouveau département"],
    ["transfer_reason", "Motif"],
    ["effective_date", "Date effective"],
];

function DepartmentTransferReadOnly({ df }) {
    return (
        <div className="space-y-3" data-testid="department-transfer-readonly">
            {READONLY_FIELDS.filter(([key]) => df[key]).map(([key, label]) => (
                <div key={key}><Label className="form-label text-gray-500">{label}</Label><p className="text-sm">{df[key]}</p></div>
            ))}
        </div>
    );
}

function ToDepartmentField({ departments, fromDeptId, toDeptId, toDeptName, onSelect, onFreeText }) {
    const [freeText, setFreeText] = useState(false);
    return (
        <div>
            <div className="flex items-center justify-between mb-1">
                <Label className="form-label">Nouveau département</Label>
                <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
                    <Switch checked={freeText} onCheckedChange={setFreeText} data-testid="transfer-to-freetext-toggle" />
                    Saisie libre
                </label>
            </div>
            {freeText ? (
                <Input value={toDeptName} onChange={(e) => onFreeText(e.target.value)} placeholder="Nom du département..." data-testid="transfer-to-dept-free" />
            ) : (
                <Select value={toDeptId || "none"} onValueChange={onSelect}>
                    <SelectTrigger data-testid="transfer-to-dept-select"><SelectValue placeholder="Sélectionner un département" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="none">Sélectionner...</SelectItem>
                        {departments.filter((d) => d.id !== fromDeptId).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                    </SelectContent>
                </Select>
            )}
        </div>
    );
}

function useTransferFields(initial = {}, users, departments) {
    const [fields, setFields] = useState({
        employee_id: initial.employee_id || "",
        employee_name: initial.employee_name || "",
        from_department_id: initial.from_department_id || "",
        from_department_name: initial.from_department_name || "",
        to_department_id: initial.to_department_id || "",
        to_department_name: initial.to_department_name || "",
        transfer_reason: initial.transfer_reason || "",
        effective_date: initial.effective_date || new Date().toISOString().slice(0, 10),
    });
    const patch = (p) => setFields((f) => ({ ...f, ...p }));

    const selectEmployee = (userId) => {
        const user = userId ? users.find((u) => u.id === userId) : null;
        if (userId && !user) return;
        const dept = user?.department_id ? departments.find((d) => d.id === user.department_id) : null;
        patch({
            employee_id: user?.id || "",
            employee_name: user ? `${user.first_name} ${user.last_name}` : "",
            from_department_id: dept?.id || "",
            from_department_name: dept?.name || "",
        });
    };

    const selectToDept = (deptId) => {
        const dept = deptId && deptId !== "none" ? departments.find((d) => d.id === deptId) : null;
        patch({ to_department_id: dept?.id || "", to_department_name: dept?.name || "" });
    };

    return { fields, patch, selectEmployee, selectToDept };
}

export function DepartmentTransferForm({ onSubmit, initialData, readOnly, submitting, onTitleChange, allowedPriorities, ticketType, onTypeChange }) {
    const baseForm = useBaseForm("medium");
    const { users, departments } = useApp();
    const { fields, patch, selectEmployee, selectToDept } = useTransferFields(initialData?.dynamic_fields, users, departments);

    if (readOnly && initialData?.dynamic_fields) {
        return <DepartmentTransferReadOnly df={initialData.dynamic_fields} />;
    }

    const handleSubmit = (e) => {
        e.preventDefault();
        const from = fields.from_department_name || "N/A";
        const to = fields.to_department_name || "N/A";
        const autoTitle = `Transfert de département — ${fields.employee_name} : ${from} → ${to}`;
        const autoDesc = `Demande de transfert de ${fields.employee_name} du département ${from} vers ${to}.${fields.transfer_reason ? `\nMotif : ${fields.transfer_reason}.` : ""}\nDate effective : ${fields.effective_date}.`;
        onSubmit({
            title: baseForm.title || autoTitle,
            description: baseForm.description || autoDesc,
            priority: baseForm.priority,
            dynamic_fields: { ...fields },
        });
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="department-transfer-form">
            <BaseFormFields
                {...baseForm}
                onTitleChange={onTitleChange}
                allowedPriorities={allowedPriorities}
                ticketType={ticketType}
                onTypeChange={onTypeChange}
                titlePlaceholder="Transfert de département — ..."
                descriptionPlaceholder="Description auto-générée si laissée vide"
            />

            <div>
                <Label className="form-label">Collaborateur concerné</Label>
                <UserSearchSelect
                    users={users.filter((u) => u.is_active !== false)}
                    departments={departments}
                    value={fields.employee_id || null}
                    onChange={selectEmployee}
                    placeholder="Rechercher un collaborateur..."
                    data-testid="transfer-employee-select"
                />
            </div>

            <div>
                <Label className="form-label">Département actuel</Label>
                <Input
                    value={fields.from_department_name}
                    onChange={(e) => patch({ from_department_name: e.target.value, from_department_id: "" })}
                    readOnly={!!fields.from_department_id}
                    className={fields.from_department_id ? "bg-gray-50 text-gray-600" : ""}
                    placeholder="Aucun département"
                    data-testid="transfer-from-dept"
                />
            </div>

            <ToDepartmentField
                departments={departments}
                fromDeptId={fields.from_department_id}
                toDeptId={fields.to_department_id}
                toDeptName={fields.to_department_name}
                onSelect={selectToDept}
                onFreeText={(v) => patch({ to_department_name: v, to_department_id: "" })}
            />

            <div>
                <Label className="form-label">Motif du transfert</Label>
                <Textarea value={fields.transfer_reason} onChange={(e) => patch({ transfer_reason: e.target.value })} placeholder="Réorganisation interne, promotion..." rows={2} data-testid="transfer-reason" />
            </div>

            <div>
                <Label className="form-label">Date effective</Label>
                <Input type="date" value={fields.effective_date} onChange={(e) => patch({ effective_date: e.target.value })} data-testid="transfer-effective-date" />
            </div>

            <div className="flex justify-end pt-4">
                <button type="submit" disabled={submitting || !baseForm.title} className="btn-primary" data-testid="submit-transfer-btn">
                    {submitting ? "Création..." : "Créer le ticket de transfert"}
                </button>
            </div>
        </form>
    );
}
